import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Pencil, Info, Star, LogOut, Eye, Images, LayoutGrid } from "lucide-react";
import { useContent, deepMerge } from "@/content/ContentContext";
import { texts, useLang } from "@/i18n";
import { TextEditor, SingleImageEditor, ListImageEditor, FormulaEditor, ReviewsEditor, SiteEditor } from "@/admin/editors";

// Mappa inversa "testo visualizzato" -> percorso nel dizionario (es. hero.titleA)
const flatten = (obj, prefix = "", out = {}) => {
  Object.entries(obj || {}).forEach(([k, v]) => {
    const p = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object") flatten(v, p, out);
    else if (typeof v === "string") out[p] = v;
  });
  return out;
};
const getPath = (obj, path) => path.split(".").reduce((o, k) => o?.[k], obj);

const EditLayer = () => {
  const { admin, editMode, setEditMode, logout, textOverrides } = useContent();
  const { lang } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState(null);
  const [hovered, setHovered] = useState(null);

  const merged = useMemo(
    () => ({ it: deepMerge(texts.it, textOverrides.it || {}), en: deepMerge(texts.en, textOverrides.en || {}) }),
    [textOverrides]
  );
  const reverse = useMemo(() => {
    const flat = flatten(merged[lang]);
    const map = new Map();
    Object.entries(flat).forEach(([p, v]) => {
      const key = v.trim();
      if (key && !map.has(key)) map.set(key, p);
    });
    return map;
  }, [merged, lang]);

  const resolve = useCallback(
    (target) => {
      let el = target;
      for (let depth = 0; el && depth < 5; depth++, el = el.parentElement) {
        if (el.closest?.("[data-admin-ui]")) return null;
        if (el.tagName === "IMG") {
          if (el.dataset.editList) return { type: "list", el, listKey: el.dataset.editList, index: Number(el.dataset.editIndex || 0) };
          if (el.dataset.editImg) return { type: "image", el, imgKey: el.dataset.editImg };
        }
        if (el.dataset?.editFormula) return { type: "formula", el, id: el.dataset.editFormula };
        if (el.dataset?.editReviews !== undefined) return { type: "reviews", el, index: Number(el.dataset.editReviews) };
        const text = (el.textContent || "").trim();
        const path = reverse.get(text);
        if (path) return { type: "text", el, path };
      }
      const hero = target.closest?.("[data-testid=hero-section]");
      if (hero) return { type: "list", el: hero, listKey: "hero", index: 0 };
      const sapori = target.closest?.("[data-testid=sapori-slider]");
      if (sapori) return { type: "list", el: sapori, listKey: "sapori", index: 0 };
      return null;
    },
    [reverse]
  );

  useEffect(() => {
    document.body.classList.toggle("edit-mode", editMode);
    if (!editMode) {
      setHovered(null);
      return undefined;
    }
    const onMove = (e) => {
      const hit = resolve(e.target);
      setHovered((prev) => (prev === hit?.el ? prev : hit?.el || null));
    };
    const onClick = (e) => {
      if (e.target.closest("[data-admin-ui]")) return;
      const hit = resolve(e.target);
      e.preventDefault();
      e.stopPropagation();
      if (!hit) return;
      if (hit.type === "text") setDialog({ kind: "text", path: hit.path, values: { it: getPath(merged.it, hit.path) ?? "", en: getPath(merged.en, hit.path) ?? "" } });
      if (hit.type === "image") setDialog({ kind: "image", imgKey: hit.imgKey });
      if (hit.type === "list") setDialog({ kind: "list", listKey: hit.listKey, index: hit.index });
      if (hit.type === "formula") setDialog({ kind: "formula", id: hit.id });
      if (hit.type === "reviews") setDialog({ kind: "reviews", index: hit.index });
    };
    document.addEventListener("mousemove", onMove, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("mousemove", onMove, true);
      document.removeEventListener("click", onClick, true);
    };
  }, [editMode, resolve, merged]);

  useEffect(() => {
    document.querySelectorAll(".edit-hover").forEach((n) => n.classList.remove("edit-hover"));
    if (hovered) hovered.classList.add("edit-hover");
  }, [hovered]);

  if (!admin || location.pathname === "/admin") return null;

  const Btn = ({ onClick, icon: Icon, label, active, testId }) => (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-[0.16em] transition-colors ${
        active ? "bg-sand text-anthracite" : "text-ivory/85 hover:bg-ivory/10"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <>
      <div
        className="fixed bottom-5 left-5 z-[110] flex items-center gap-1 bg-anthracite/95 backdrop-blur border border-ivory/15 p-1 shadow-2xl"
        data-admin-ui="true"
        data-testid="admin-toolbar"
      >
        <Btn onClick={() => setEditMode(!editMode)} icon={editMode ? Eye : Pencil} label={editMode ? "Modifica attiva" : "Modifica"} active={editMode} testId="toolbar-toggle-edit" />
        <Btn onClick={() => setDialog({ kind: "list", listKey: "hero", index: 0 })} icon={Images} label="Hero" testId="toolbar-hero" />
        <Btn onClick={() => setDialog({ kind: "list", listKey: "gallery", index: 0 })} icon={LayoutGrid} label="Gallery" testId="toolbar-gallery" />
        <Btn onClick={() => setDialog({ kind: "site" })} icon={Info} label="Info locale" testId="toolbar-site" />
        <Btn onClick={() => setDialog({ kind: "reviews", index: 0 })} icon={Star} label="Recensioni" testId="toolbar-reviews" />
        <Btn onClick={() => { logout(); navigate("/admin"); }} icon={LogOut} label="Esci" testId="toolbar-logout" />
      </div>
      {editMode && (
        <p className="fixed top-24 md:top-28 left-1/2 -translate-x-1/2 z-[105] bg-bordeaux text-ivory text-[11px] uppercase tracking-[0.2em] px-4 py-2 shadow-lg pointer-events-none" data-admin-ui="true" data-testid="edit-mode-banner">
          Clicca su un testo o una foto per modificarli
        </p>
      )}
      {dialog?.kind === "text" && <TextEditor path={dialog.path} values={dialog.values} onClose={() => setDialog(null)} />}
      {dialog?.kind === "image" && <SingleImageEditor imgKey={dialog.imgKey} onClose={() => setDialog(null)} />}
      {dialog?.kind === "list" && <ListImageEditor listKey={dialog.listKey} startIndex={dialog.index} onClose={() => setDialog(null)} />}
      {dialog?.kind === "formula" && <FormulaEditor formulaId={dialog.id} onClose={() => setDialog(null)} />}
      {dialog?.kind === "reviews" && <ReviewsEditor startIndex={dialog.index} onClose={() => setDialog(null)} />}
      {dialog?.kind === "site" && <SiteEditor onClose={() => setDialog(null)} />}
    </>
  );
};

export default EditLayer;
